import { i18n } from "~/localization/i18n";
import { trackAppInsightsTelemetry, TRACK_METHOD } from "~/logging/applicationInsightsService";
import { setTelemetry, setTranslator } from "@viedoc/me-core/form";

/**
 * Connects the rule engine's injected seams to this app's implementations.
 *
 * The engine no longer imports i18next or Application Insights directly — both
 * are module-load singletons bound to the web bootstrap, which a shared package
 * cannot depend on. It calls injected seams instead, and this is where the web
 * app fills them in.
 *
 * The engine's telemetry seam names two intents (`trackException`,
 * `trackTrace`) rather than taking a TRACK_METHOD, so mapping those onto
 * Application Insights stays here. That keeps TRACK_METHOD out of the package
 * entirely.
 *
 * Must be called once, before anything renders a form. `src/index.jsx` does it.
 *
 * `src/print-entry.jsx` is a second composition root but deliberately does not
 * call this: it renders `PrintForm`, which is force-show-all and never calls
 * `convertFormValidation` (its own test asserts that), so neither seam is
 * reached. If print ever starts evaluating expressions, it needs this call too,
 * and the failure mode would be silent.
 *
 * This used to claim as well that nothing in print's tree imports the
 * expression sandbox. That became false when the app moved to
 * `@viedoc/me-core/form`: FormSubmitPage.jsx swapped a leaf import for the
 * barrel, the barrel re-exports convertFormValidation, and that reaches the
 * sandbox, which runs extendArrayWithContains() at module load. Review measured
 * the print chunk growing 764,359 to 772,444 bytes with Array.prototype.contains
 * in it (PR 41202, thread 299328).
 *
 * The package now declares arrayContains.js as its only side-effectful module,
 * so Rollup may drop the re-exports print does not use instead of keeping the
 * whole barrel. Measured back at 764,363 bytes with the marker absent. Stated
 * as a measurement, not as a claim about the import graph, because the import
 * graph no longer settles it: tree-shaking does. To re-check, build and grep
 * the chunk that print-entry-*.js statically imports for
 * "Array.prototype.contains".
 *
 * ⚠️ Both seams have deliberately safe defaults, which means forgetting to call
 * this FAILS SILENTLY: validation messages degrade from localized text to raw
 * keys ("validation.required"), and expression errors and type mismatches stop
 * being reported. No engine test would catch it — they assert on the keys. This
 * is not hypothetical: the Viedoc.Me.ReactNative POC built both seams and never
 * called the setters, so it shipped raw keys. `formEngineWiring.test.js` guards
 * it.
 */
export function configureFormEngine() {
    setTranslator((key, params) => i18n.t(key, params));

    setTelemetry({
        trackException(payload) {
            trackAppInsightsTelemetry(payload, TRACK_METHOD.EXCEPTION);
        },
        trackTrace(payload) {
            trackAppInsightsTelemetry(payload, TRACK_METHOD.TRACE);
        },
    });
}
